import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand/logo";
import { z } from "zod";
import { AuthLayout } from "@/components/auth/auth-layout";
import { AuthField } from "@/components/auth/auth-field";
import { SocialButtons } from "@/components/auth/social-buttons";
import { LoadingButton } from "@/components/auth/loading-button";
import { setSession, type Role } from "@/lib/session";

const schema = z.object({
  email: z.string().trim().min(1, "Email is required").email("Enter a valid email").max(255),
  password: z.string().min(1, "Password is required").max(72),
});

export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({
    meta: [
      { title: "Sign in — Spayzo" },
      { name: "description", content: "Sign in to your Spayzo account to manage bookings, favorites and listings." },
      { property: "og:title", content: "Sign in — Spayzo" },
      { property: "og:description", content: "Access your Spayzo bookings and listings." },
      { property: "og:url", content: "https://spayzo.lovable.app/login" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/login" }],
  }),
});

function LoginPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [values, setValues] = useState({ email: "", password: "" });
  const [role, setRole] = useState<Role>("user");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(values);
    if (!result.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof typeof errors;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 500));
    setSession({ userId: result.data.email, email: result.data.email, role });
    setSubmitting(false);
    const target = role === "admin" ? "/admin" : role === "merchant" ? "/merchant" : "/dashboard";
    router.navigate({ to: target });
  };

  return (
    <AuthLayout side="right" hideFooter>
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        <div className="mb-8">
          <Logo to="/" className="mb-4 h-8 w-auto text-foreground" />
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("auth.welcomeBack")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("auth.newToSpayzo")}{" "}
            <Link to="/register" className="font-medium text-primary hover:underline">
              {t("auth.createAccount")}
            </Link>
          </p>
        </div>

        <SocialButtons />

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.or")}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <AuthField
            label={t("common.email")}
            type="email"
            icon={Mail}
            placeholder={t("auth.emailPlaceholder")}
            autoComplete="email"
            value={values.email}
            onChange={update("email")}
            error={errors.email}
          />
          <AuthField
            label={t("common.password")}
            type="password"
            icon={Lock}
            placeholder={t("auth.passwordPlaceholder")}
            autoComplete="current-password"
            value={values.password}
            onChange={update("password")}
            error={errors.password}
            hint={
              <Link to="/reset-password" className="text-xs font-medium text-primary hover:underline">
                {t("auth.forgotPassword")}
              </Link>
            }
          />

          <LoadingButton
            type="submit"
            loading={submitting}
            className="mt-2 h-12 w-full rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-95 active:scale-[0.99] disabled:opacity-70"
          >
            {t("auth.signInBtn")} <ArrowRight className="h-4 w-4" />
          </LoadingButton>
        </form>
      </div>
    </AuthLayout>
  );
}
